import { Clock, PauseCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Conversation } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { durationParts, headlineClock, slaClocks, type SlaClock } from '../lib/sla';

type Ticket = Pick<Conversation, 'status' | 'firstStaffReplyAt' | 'sla'>;

/** "First reply due in 2h 5m", "Resolution overdue by 1d 3h", "First reply paused"... */
function useClockText() {
  const { t } = useTranslation('conversations');
  const duration = (fromMs: number, toMs: number) => {
    const parts = durationParts(fromMs, toMs);
    if (parts.days > 0) return t('sla.time.days', { ...parts });
    if (parts.hours > 0) return t('sla.time.hours', { ...parts });
    return t('sla.time.minutes', { ...parts });
  };
  return (clock: SlaClock, now: number) => {
    const target = t(`sla.target.${clock.target}`);
    const at = clock.at ? Date.parse(clock.at) : now;
    if (clock.state === 'due') return t('sla.due', { target, time: duration(now, at) });
    if (clock.state === 'overdue') return t('sla.overdue', { target, time: duration(at, now) });
    return t(`sla.${clock.state}`, { target });
  };
}

/** The row's countdown or breach badge for a ticket; nothing for chats or stores without targets. */
export function SlaBadge({ conversation, now }: { conversation: Ticket; now: number }) {
  const format = useFormat();
  const text = useClockText();
  const clock = headlineClock(slaClocks(conversation, now));
  if (!clock) return null;
  const Icon = clock.state === 'paused' ? PauseCircle : Clock;
  return (
    <Badge
      variant={clock.state === 'overdue' ? 'destructive' : 'outline'}
      className={cn(
        'h-5 gap-1 px-1.5 text-[10px]',
        clock.state === 'paused' && 'text-muted-foreground',
      )}
      title={clock.at ? format.dateTime(clock.at) : undefined}
    >
      <Icon className="size-3" aria-hidden />
      {text(clock, now)}
    </Badge>
  );
}

/** Every target of a ticket with its due time, for the thread header. */
export function SlaSummary({ conversation, now }: { conversation: Ticket; now: number }) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const text = useClockText();
  const clocks = slaClocks(conversation, now);
  if (clocks.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" aria-label={t('sla.label')}>
      {clocks.map((clock) => (
        <li
          key={clock.target}
          className={cn(
            'inline-flex items-center gap-1',
            clock.state === 'overdue' || clock.state === 'late'
              ? 'text-destructive'
              : 'text-muted-foreground',
          )}
        >
          {clock.state === 'paused' ? (
            <PauseCircle className="size-3" aria-hidden />
          ) : (
            <Clock className="size-3" aria-hidden />
          )}
          <span>{text(clock, now)}</span>
          {clock.at && (clock.state === 'due' || clock.state === 'overdue') ? (
            <time dateTime={clock.at} title={format.utc(clock.at)} className="opacity-70">
              ({format.dateTime(clock.at)})
            </time>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
